import React, { useEffect, useRef, useState } from "react";
import ReactSelect from "react-select";
import {
  Filter,
  Package,
  Ruler,
  ArrowUpDown,
  ListFilter,
  ListChecks,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchProcessItemsStock,
  fetchStockItems,
  fetchStockItemSizes,
  getProcessItemsStock,
  getTotalProcessItemsStock,
  getProcessItemsStockFilter,
  setProcessItemsStockFilter,
  getStockItemOptions,
  getStockItemSizeOptions,
  getStockLoading,
} from "../../features/stock/stockSlice";
import {
  fetchProcessDefinitions,
  getProcessDefinitions,
} from "../../features/processDefinitions/processDefinitionsSlice";
import { getSelectedBranch } from "../../features/settings/settingsSlice";
import { filterSelectStyles, toastStyles } from "../../static/styles";
import { exportModuleReport } from "../../services/reportExport.service";
import { localizedProcessName } from "../../lib/utils";
import { t } from "../../locales";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { ProcessItemsStockFilter, ProcessItemStockRow } from "../../types/stock";

const ProcessItemsStockList = () => {
  const dispatch = useAppDispatch();

  const rows = useAppSelector(getProcessItemsStock);
  const totalRows = useAppSelector(getTotalProcessItemsStock);
  const filter = useAppSelector(getProcessItemsStockFilter);
  const loading = useAppSelector(getStockLoading);
  const selectedBranch = useAppSelector(getSelectedBranch);
  const itemOptions = useAppSelector(getStockItemOptions);
  const itemSizeOptions = useAppSelector(getStockItemSizeOptions);
  const definitions = useAppSelector(getProcessDefinitions);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedFilterItem, setSelectedFilterItem] = useState<{ value: number; label: string } | null>(null);
  const [selectedFilterItemSize, setSelectedFilterItemSize] = useState<{ value: number; label: string } | null>(null);

  const sortByOptions = [
    { value: "item_name", label: t.stock.sortBy.itemName },
    { value: "ordered_qty", label: t.stock.sortBy.orderedQty },
    { value: "received_qty", label: t.stock.sortBy.qtyReceived },
    { value: "routed_qty", label: t.stock.sortBy.routedQty },
    { value: "available_qty", label: t.stock.sortBy.availableQty },
    { value: "pending_qty", label: t.stock.sortBy.pendingQty },
  ];

  const sortOrderOptions = [
    { value: "asc", label: t.stock.sortOrderOptions.asc },
    { value: "desc", label: t.stock.sortOrderOptions.desc },
  ];

  // Process filter is generated from process_definitions at runtime, same as
  // the dashboard tiles and order/receive forms — no hardcoded process list.
  const processOptions: { value: number | "all"; label: string }[] = [
    { value: "all", label: t.stock.processAll },
    ...[...definitions]
      .filter((d) => d.is_active)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((d) => ({ value: d.id, label: localizedProcessName(d) })),
  ];

  const [selectedFilterProcess, setSelectedFilterProcess] = useState<{ value: number | "all"; label: string } | null>(
    processOptions.find((o) => o.value === (filter.process_definition_id ?? "all")) || processOptions[0]
  );

  const { control, handleSubmit, reset, getValues } = useForm<ProcessItemsStockFilter>({
    defaultValues: { ...filter },
  });

  const doFetch = () => {
    dispatch(
      fetchProcessItemsStock({
        page,
        size: pageSize,
        process_definition_id: filter.process_definition_id,
        item_id: filter.item_id,
        item_size_id: filter.item_size_id,
        sortBy: filter.sortBy,
        sortOrder: filter.sortOrder,
      })
    );
  };

  const prevBranchIdRef = useRef<number | null>(null);
  const selectedBranchId = selectedBranch?.id ?? null;

  useEffect(() => {
    if (!selectedBranchId) return;
    const prevBranchId = prevBranchIdRef.current;
    prevBranchIdRef.current = selectedBranchId;
    const isBranchChange =
      prevBranchId !== null && prevBranchId !== selectedBranchId;
    if (isBranchChange && page !== 1) {
      setPage(1);
      return;
    }
    doFetch();
  }, [page, pageSize, filter, selectedBranchId]);

  useEffect(() => {
    dispatch(fetchStockItems());
    dispatch(fetchStockItemSizes());
    if (definitions.length === 0) {
      dispatch(fetchProcessDefinitions());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (filter.item_id && itemOptions.length > 0) {
      setSelectedFilterItem(itemOptions.find((o) => o.value === filter.item_id) || null);
    } else {
      setSelectedFilterItem(null);
    }
  }, [filter.item_id, itemOptions]);

  useEffect(() => {
    if (filter.item_size_id && itemSizeOptions.length > 0) {
      setSelectedFilterItemSize(itemSizeOptions.find((o) => o.value === filter.item_size_id) || null);
    } else {
      setSelectedFilterItemSize(null);
    }
  }, [filter.item_size_id, itemSizeOptions]);

  useEffect(() => {
    setSelectedFilterProcess(
      processOptions.find((o) => o.value === (filter.process_definition_id ?? "all")) || processOptions[0]
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.process_definition_id, definitions]);

  const onApplyFilters = (data: ProcessItemsStockFilter) => {
    dispatch(
      setProcessItemsStockFilter({
        process_definition_id:
          selectedFilterProcess?.value && selectedFilterProcess.value !== "all"
            ? selectedFilterProcess.value
            : null,
        item_id: selectedFilterItem?.value || null,
        item_size_id: selectedFilterItemSize?.value || null,
        sortBy: data.sortBy,
        sortOrder: data.sortOrder,
      })
    );
    setPage(1);
  };

  const onExportReport = () => {
    const values = getValues();
    exportModuleReport(
      "/v1/stock/process-items/report-pdf",
      {
        process_definition_id:
          selectedFilterProcess?.value && selectedFilterProcess.value !== "all"
            ? selectedFilterProcess.value
            : null,
        item_id: selectedFilterItem?.value || null,
        item_size_id: selectedFilterItemSize?.value || null,
        sortBy: values.sortBy,
        sortOrder: values.sortOrder,
      },
      toastStyles,
      setIsExporting,
      undefined,
      { requireDateRange: false }
    );
  };

  const handleClearFilters = () => {
    const defaults: ProcessItemsStockFilter = {
      process_definition_id: null,
      item_id: null,
      item_size_id: null,
      sortBy: "item_name",
      sortOrder: "asc",
    };
    reset(defaults);
    setSelectedFilterItem(null);
    setSelectedFilterItemSize(null);
    setSelectedFilterProcess(processOptions[0]);
    dispatch(setProcessItemsStockFilter(defaults));
    setPage(1);
  };

  const totalPages = Math.ceil(totalRows / pageSize) || 1;

  const getVisiblePages = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push("...");
      for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
        pages.push(i);
      }
      if (page < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const start = totalRows === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalRows);

  const showProcessColumn = !filter.process_definition_id;

  return (
    <div className="space-y-4">
      {/* Top bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-3 ml-auto">
          <Button
            variant="outline"
            className="h-10 px-5 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67]"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            {t.stock.filterToggle}
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t.stock.filterTitle}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-4">
            <form onSubmit={handleSubmit(onApplyFilters)}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.stock.processLabel}
                  </label>
                  <div className="relative">
                    <ListChecks className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <ReactSelect
                      styles={filterSelectStyles}
                      options={processOptions}
                      placeholder={t.stock.selectProcess}
                      value={selectedFilterProcess}
                      onChange={(opt: any) => setSelectedFilterProcess(opt || processOptions[0])}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.stock.itemLabel}
                  </label>
                  <div className="relative">
                    <Package className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <ReactSelect
                      styles={filterSelectStyles}
                      options={itemOptions}
                      isClearable
                      placeholder={t.stock.selectItem}
                      value={selectedFilterItem}
                      onChange={(opt: any) => setSelectedFilterItem(opt || null)}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.stock.itemSizeLabel}
                  </label>
                  <div className="relative">
                    <Ruler className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <ReactSelect
                      styles={filterSelectStyles}
                      options={itemSizeOptions}
                      isClearable
                      placeholder={t.stock.selectItemSize}
                      value={selectedFilterItemSize}
                      onChange={(opt: any) => setSelectedFilterItemSize(opt || null)}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.stock.sortByLabel}
                  </label>
                  <div className="relative">
                    <ListFilter className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller
                      name="sortBy"
                      control={control}
                      render={({ field }) => (
                        <ReactSelect
                          styles={filterSelectStyles}
                          options={sortByOptions}
                          value={sortByOptions.find((o) => o.value === field.value)}
                          onChange={(opt: any) => field.onChange(opt?.value || "item_name")}
                        />
                      )}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.stock.sortOrderLabel}
                  </label>
                  <div className="relative">
                    <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller
                      name="sortOrder"
                      control={control}
                      render={({ field }) => (
                        <ReactSelect
                          styles={filterSelectStyles}
                          options={sortOrderOptions}
                          value={sortOrderOptions.find((o) => o.value === field.value)}
                          onChange={(opt: any) => field.onChange(opt?.value || "asc")}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-4 border-t pt-4 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 px-5 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67]"
                  onClick={handleClearFilters}
                >
                  {t.stock.clearFilters}
                </Button>
                <Button
                  type="button"
                  disabled={isExporting}
                  onClick={onExportReport}
                  className="h-10 px-5 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67] text-white"
                >
                  {isExporting ? "..." : t.stock.exportReport}
                </Button>
                <Button
                  type="submit"
                  className="h-10 px-5 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67]"
                >
                  {t.stock.applyFilters}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {loading && rows.length === 0 ? (
          <Card>
            <CardContent className="py-8">
              <div className="flex items-center justify-center gap-2 text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />
                {t.stock.loading}
              </div>
            </CardContent>
          </Card>
        ) : !loading && rows.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-500">
              {t.stock.noRecords}
            </CardContent>
          </Card>
        ) : (
          <div className={`space-y-3${loading ? " opacity-50 pointer-events-none" : ""}`}>
            {rows.map((row: ProcessItemStockRow, index: number) => (
              <Card key={`${row.process_definition_id}-${row.item_id}-${row.item_size_id ?? "x"}-${index}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <span className="font-medium text-gray-900">
                        {row.item_name || "-"}
                      </span>
                      <div className="text-sm text-gray-500">
                        #{(page - 1) * pageSize + index + 1}
                        {row.item_size != null ? ` · ${row.item_size}` : ""}
                      </div>
                    </div>
                    {showProcessColumn && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-blue-50 text-[#113F67] font-semibold">
                        {row.process_definition_name || "-"}
                      </span>
                    )}
                  </div>
                  <div className="mt-3 pt-3 border-t grid grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t.stock.orderedQtyLabel}</span>
                      <span className="font-semibold text-gray-900">{row.ordered_qty}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t.stock.qtyReceivedLabel}</span>
                      <span className="font-semibold text-gray-900">{row.received_qty}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t.stock.routedQtyLabel}</span>
                      <span className="font-semibold text-gray-900">{row.routed_qty}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t.stock.availableQtyLabel}</span>
                      <span className="font-semibold text-[#113F67]">{row.available_qty}</span>
                    </div>
                    <div className="flex justify-between col-span-2">
                      <span className="text-gray-500">{t.stock.pendingQtyLabel}</span>
                      <span
                        className={`font-semibold ${row.pending_qty > 0 ? "text-amber-600" : "text-gray-500"}`}
                      >
                        {row.pending_qty}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      <Card className="hidden md:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">{t.stock.table.srNo}</TableHead>
                  {showProcessColumn && <TableHead>{t.stock.processLabel}</TableHead>}
                  <TableHead>{t.stock.itemLabel}</TableHead>
                  <TableHead>{t.stock.itemSizeLabel}</TableHead>
                  <TableHead className="text-right">{t.stock.orderedQtyLabel}</TableHead>
                  <TableHead className="text-right">{t.stock.qtyReceivedLabel}</TableHead>
                  <TableHead className="text-right">{t.stock.routedQtyLabel}</TableHead>
                  <TableHead className="text-right">{t.stock.availableQtyLabel}</TableHead>
                  <TableHead className="text-right">{t.stock.pendingQtyLabel}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={showProcessColumn ? 9 : 8} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2 text-gray-500">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />
                        {t.stock.loading}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={showProcessColumn ? 9 : 8}
                      className="text-center py-8 text-gray-500"
                    >
                      {t.stock.noRecords}
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row: ProcessItemStockRow, index: number) => (
                    <TableRow
                      className="align-top"
                      key={`${row.process_definition_id}-${row.item_id}-${row.item_size_id ?? "x"}-${index}`}
                    >
                      <TableCell className="font-medium text-gray-500">
                        {(page - 1) * pageSize + index + 1}
                      </TableCell>
                      {showProcessColumn && (
                        <TableCell>
                          <span className="px-2 py-0.5 text-xs rounded-full bg-blue-50 text-[#113F67] font-semibold">
                            {row.process_definition_name || "-"}
                          </span>
                        </TableCell>
                      )}
                      <TableCell>
                        <div>
                          <span className="font-medium text-gray-900">
                            {row.item_name || "-"}
                          </span>
                          <div className="text-sm text-gray-500">ID: {row.item_id}</div>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-700">
                        {row.item_size != null ? row.item_size : "—"}
                      </TableCell>
                      <TableCell className="text-right text-gray-700">{row.ordered_qty}</TableCell>
                      <TableCell className="text-right text-gray-700">{row.received_qty}</TableCell>
                      <TableCell className="text-right text-gray-700">{row.routed_qty}</TableCell>
                      <TableCell className="text-right font-semibold text-[#113F67]">
                        {row.available_qty}
                      </TableCell>
                      <TableCell
                        className={`text-right font-semibold ${
                          row.pending_qty > 0 ? "text-amber-600" : "text-gray-500"
                        }`}
                      >
                        {row.pending_qty}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {!loading && rows.length > 0 && (
        <div className="flex flex-wrap items-center justify-end md:justify-between gap-4">
          <div className="hidden md:flex items-center gap-2 text-sm text-gray-600">
            <span>
              {start}-{end} of {totalRows} {t.stock.pageOfLabel}
            </span>
            <span>{t.stock.pageSizeLabel}</span>
            <Select
              value={String(pageSize)}
              onValueChange={(val) => {
                setPageSize(Number(val));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-20 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setPage(1)}
              disabled={page === 1}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {getVisiblePages().map((p, i) =>
              typeof p === "string" ? (
                <span key={`ellipsis-${i}`} className="px-2 text-gray-400">
                  ...
                </span>
              ) : (
                <Button
                  key={p}
                  variant={page === p ? "default" : "outline"}
                  className={`w-8 h-8 p-0 ${
                    page === p ? "bg-[#113F67] hover:bg-[#0d2f4d]" : ""
                  }`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </Button>
              )
            )}
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessItemsStockList;
